/* Progressive display type enhancement. Text stays semantic and searchable;
   existing links, accent spans and explicit line breaks remain intact. */
(() => {
  if (window.TerlatoDisplayType) return;

  const selector = [
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6', '.h0', '.h1', '.h2',
    '.gf-display', '.gf-h1', '.gf-h2', '[data-gf-display]',
    '.gf-how-it-works__step', '.gf-corp-steps__label',
    '.gf-corp-steps__step-title', '.gf-finder-steps__number'
  ].join(',');
  const excluded = 'script,style,svg,button,input,textarea,select,[contenteditable="true"],[aria-hidden="true"],.visually-hidden,.gf-visually-hidden';
  const processed = new WeakMap();
  const pending = new Set();
  let frame = null;
  let observer;

  function apply(element, originalText) {
    // AJAX can replace a portion of an existing title. Remove only our own
    // wrappers before rebuilding, leaving the template's DOM and events intact.
    element.querySelectorAll('.gf-display-word').forEach(word => {
      word.replaceWith(document.createTextNode(word.textContent));
    });
    element.normalize();

    const events = [];
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          if (node.matches(excluded)) return NodeFilter.FILTER_REJECT;
          return node.tagName === 'BR' ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
        }
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    while (walker.nextNode()) events.push(walker.currentNode);

    let atWordStart = true;
    events.forEach(node => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        atWordStart = true;
        return;
      }
      const fragment = document.createDocumentFragment();
      node.data.split(/(\s+)/u).forEach(part => {
        if (!part) return;
        if (/^\s+$/u.test(part)) {
          fragment.append(document.createTextNode(part));
          atWordStart = true;
          return;
        }
        const match = atWordStart && part.match(/^([^\p{L}\p{N}]*)([\p{L}\p{N}])([\s\S]*)$/u);
        const word = document.createElement('span');
        word.className = 'gf-display-word';
        if (match) {
          const initial = document.createElement('span');
          initial.className = 'gf-display-initial';
          initial.textContent = match[1] + match[2];
          word.append(initial);
          if (match[3]) {
            const rest = document.createElement('span');
            rest.className = 'gf-display-rest';
            rest.textContent = match[3];
            word.append(rest);
          }
          atWordStart = false;
        } else {
          const rest = document.createElement('span');
          rest.className = 'gf-display-rest';
          rest.textContent = part;
          word.append(rest);
        }
        fragment.append(word);
      });
      node.replaceWith(fragment);
    });
    element.classList.add('gf-display-type');
    processed.set(element, originalText);
  }

  function collect(root, candidates) {
    if (!root) return;
    if (root.nodeType === Node.ELEMENT_NODE) {
      const heading = root.closest(selector);
      if (heading) candidates.add(heading);
    }
    root.querySelectorAll?.(selector).forEach(element => candidates.add(element));
  }

  function enhance(roots) {
    const candidates = new Set();
    roots.forEach(root => collect(root, candidates));
    const updates = [];

    // Read every candidate's font before changing the DOM. Interleaving a font
    // lookup with each heading rewrite makes the browser recalculate styles
    // repeatedly. Overlapping mutation roots also share a single candidate.
    candidates.forEach(element => {
      if (element.closest(excluded)) return;
      const originalText = element.textContent;
      if (!originalText.trim()) return;
      if (processed.get(element) === originalText && element.querySelector('.gf-display-word')) return;
      if (!getComputedStyle(element).fontFamily.toLowerCase().includes('mightmakesright')) return;
      updates.push({ element, originalText });
    });
    updates.forEach(({ element, originalText }) => apply(element, originalText));
  }

  function flush() {
    frame = null;
    observer?.disconnect();
    enhance(pending);
    pending.clear();
    observer?.observe(document.body, { childList: true, characterData: true, subtree: true });
  }

  function schedule(root) {
    if (!root) return;
    pending.add(root.nodeType === Node.TEXT_NODE ? root.parentElement : root);
    if (frame === null) frame = requestAnimationFrame(flush);
  }

  function start() {
    enhance([document.body]);
    observer = new MutationObserver(records => {
      records.forEach(record => {
        if (record.type === 'characterData') schedule(record.target);
        else {
          if (record.target.closest?.(selector)) schedule(record.target);
          record.addedNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) schedule(node);
          });
        }
      });
    });
    observer.observe(document.body, { childList: true, characterData: true, subtree: true });
    document.addEventListener('shopify:section:load', event => schedule(event.target));
  }

  window.TerlatoDisplayType = { refresh: schedule };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
